// Motor común de los filtros de texto: cómo se parte lo que se pega y cómo casa
// cada término contra un campo.
//
// Vive aquí, y no en el módulo de informes donde nació, porque las cuatro
// pantallas con buscador necesitan exactamente la misma regla. Cuando cada una
// tenía la suya, escribir un número de pedido y luego un artículo devolvía la
// UNIÓN de los dos en vez de su intersección: el buscador amontonaba pedido,
// cliente y artículos en un solo texto y ahí dentro sólo cabe un OR.
//
// La regla es una sola frase: OR entre los términos de un mismo campo, AND
// entre campos distintos. El AND lo compone quien consume los predicados
// (`every`), no este módulo.

/** Lo que separa un término de otro al pegar una lista.
 *
 *  Se aceptan los cuatro porque el usuario no elige el formato de lo que copia:
 *  una columna de Excel trae saltos de línea, una fila trae tabuladores y una
 *  lista escrita a mano trae comas o punto y coma. */
export const SEPARADORES = /[\n\r,;\t]+/

/** Parte un pegado en términos, sin vacíos ni espacios de sobra. */
export function partirTerminos(bruto: string): string[] {
  return bruto.split(SEPARADORES).map(s => s.trim()).filter(Boolean)
}

/**
 * Los valores completos que toma un campo en los datos ya cargados.
 *
 * Es el catálogo que decide qué término casa exacto y cuál por subcadena. Se
 * construye de las filas que hay en pantalla, no de un maestro: lo que importa
 * es si el término es un valor entero de ESTE campo aquí.
 */
export function catalogoDe<T>(filas: T[], leer: (fila: T) => string | undefined): Set<string> {
  const out = new Set<string>()
  for (const fila of filas) {
    const v = leer(fila)
    if (v != null && v !== '') out.add(String(v).trim().toLowerCase())
  }
  return out
}

/**
 * Predicado de UN campo de texto: ¿casa la fila con alguno de sus términos?
 *
 * **Un término que es un valor completo del campo casa por igualdad; uno que no
 * lo es, por subcadena.** Sin esa distinción no hay forma de pedir un artículo y
 * sólo ese: en consumo de producción conviven `ARF32120414` y `XARF32120414`, y
 * el código del uno está dentro del otro, así que filtrar por el primero traía
 * también el segundo. Lo mismo con dos facturas `F-100` y `F-1000`.
 *
 * Sin catálogo hay que decidir a ciegas, y ahí manda el recuento: **un término
 * casa por subcadena y dos o más casan exactos**. Detrás está la intención de
 * quien escribe — teclear media referencia es explorar; pegar una lista es traer
 * códigos que ya se tienen enteros.
 */
export function predicadoTexto(
  terminos: string[],
  catalogo?: Set<string>,
): (texto: string | undefined) => boolean {
  if (terminos.length === 0) return () => true

  const agujas = terminos.map(t => t.trim().toLowerCase()).filter(Boolean)
  if (agujas.length === 0) return () => true

  const exactas = catalogo
    ? new Set(agujas.filter(a => catalogo.has(a)))
    : new Set(agujas.length >= 2 ? agujas : [])
  const parciales = agujas.filter(a => !exactas.has(a))

  return (texto) => {
    const t = String(texto ?? '').trim().toLowerCase()
    if (!t) return false
    if (exactas.has(t)) return true
    return parciales.some(a => t.includes(a))
  }
}

/**
 * Predicado de un campo que se lee de VARIAS columnas de la fila.
 *
 * Es el caso del artículo, que se compara contra el código crudo y contra el
 * formateado para el rol avp. Ojo: esto NO es el amasijo de antes. Aquí las
 * columnas son distintas grafías del MISMO dato, no campos con significados
 * diferentes; juntar el pedido con el cliente es lo que estaba mal.
 */
export function predicadoVariasGrafias(
  terminos: string[],
  catalogo?: Set<string>,
): (grafias: (string | undefined)[]) => boolean {
  const casa = predicadoTexto(terminos, catalogo)
  if (terminos.length === 0) return () => true
  return (grafias) => grafias.some(g => casa(g))
}

/**
 * Predicado de un campo que YA es una concatenación, como
 * `articulos_busqueda` (artículo + descripción de todas las líneas del pedido).
 *
 * Aquí la coincidencia es siempre por subcadena, y no es una excepción
 * caprichosa: el campo no tiene «valores completos» contra los que comparar —su
 * valor entero es la lista— así que exigir igualdad no casaría jamás.
 *
 * Que este campo sea un amasijo está bien, porque amasija **el mismo dato
 * repetido** (los artículos del pedido). Lo que estaba mal era amasijar el
 * número de pedido con el cliente y con los artículos: significados distintos
 * en un solo saco, donde ya no cabe preguntar por dos cosas a la vez.
 */
export function predicadoTextoAgregado(
  terminos: string[],
): (texto: string | undefined) => boolean {
  const agujas = terminos.map(t => t.trim().toLowerCase()).filter(Boolean)
  if (agujas.length === 0) return () => true
  return (texto) => {
    const t = String(texto ?? '').toLowerCase()
    return !!t && agujas.some(a => t.includes(a))
  }
}

/** Una opción de catálogo, en lo mínimo que necesita el pegado. */
export interface OpcionResoluble {
  valor: string
  titulo: string
}

/**
 * Qué términos añade un pegado, resolviéndolos contra el catálogo.
 *
 * Cada línea pegada se busca en el catálogo por su VALOR (el código crudo de la
 * vista) y por su TÍTULO (el que se enseña, que en el rol avp va formateado:
 * `QARF…` se ve como `ARF…`). Quien pega la referencia tal y como sale de D365
 * tiene que acabar eligiendo la misma opción que habría elegido de la lista, y
 * quien pega lo que ve en pantalla también.
 *
 * Lo que el catálogo no reconoce entra como texto libre si el campo lo admite, y
 * se descarta si no: inventar un valor que ningún dato tiene sólo produce un
 * filtro que no encuentra nada y no explica por qué.
 *
 * No devuelve duplicados ni repite lo ya seleccionado.
 */
export function resolverPegado(
  partes: readonly string[],
  opciones: readonly OpcionResoluble[],
  seleccion: readonly string[],
  permitirLibre: boolean,
): string[] {
  const porValor = new Map<string, string>()
  for (const o of opciones) {
    porValor.set(o.valor.toLowerCase(), o.valor)
    if (!porValor.has(o.titulo.toLowerCase())) porValor.set(o.titulo.toLowerCase(), o.valor)
  }
  const nuevos: string[] = []
  for (const parte of partes) {
    const valor = porValor.get(parte.toLowerCase()) ?? (permitirLibre ? parte : null)
    if (valor === null || seleccion.includes(valor) || nuevos.includes(valor)) continue
    nuevos.push(valor)
  }
  return nuevos
}
